<template>
  <div class="hpr">
    <header class="hpr-head">
      <div class="hpr-head__in">
        <span class="hpr-brand">{{ pr.vendor_name || 'Payment' }}</span>
        <span class="hpr-secure">
          <svg viewBox="0 0 24 24" class="hpr-lock"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
          Secure payment
        </span>
      </div>
    </header>

    <main class="hpr-wrap">
      <div v-if="loading" class="hpr-card hpr-muted">Loading…</div>

      <div v-else-if="notFound" class="hpr-card">
        <h1 class="hpr-title">This payment link is not valid</h1>
        <p class="hpr-p">Check the link you were sent, or ask the sender for a new one.</p>
      </div>

      <div v-else-if="loadError" class="hpr-card">
        <h1 class="hpr-title">Something went wrong</h1>
        <p class="hpr-p">{{ loadError }}</p>
        <button class="hpr-btn hpr-btn--ghost" @click="load">Try again</button>
      </div>

      <div v-else class="hpr-card">
        <p class="hpr-from">Payment request from <b>{{ pr.vendor_name }}</b></p>
        <h1 class="hpr-title">{{ pr.description }}</h1>

        <dl class="hpr-sum">
          <div v-if="pr.vat_cents > 0"><dt>Amount</dt><dd>{{ money(pr.net_cents) }}</dd></div>
          <div v-if="pr.vat_cents > 0"><dt>VAT ({{ pr.vat_rate }}%)</dt><dd>{{ money(pr.vat_cents) }}</dd></div>
          <div class="hpr-sum__total"><dt>Total</dt><dd>{{ money(pr.amount_cents) }}</dd></div>
        </dl>
        <p v-if="pr.vat_number" class="hpr-fine">VAT number {{ pr.vat_number }}</p>

        <!-- Open: pay, or try again after a failed attempt -->
        <template v-if="isOpen && !confirming">
          <div v-if="pr.status === 'failed'" class="hpr-note hpr-note--warn">
            {{ failureText }} You can try again.
          </div>
          <button class="hpr-btn" :disabled="starting" @click="pay">
            <svg viewBox="0 0 24 24" class="hpr-btn__i"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
            {{ starting ? 'Opening secure payment…' : (pr.status === 'failed' ? 'Try again' : 'Pay ' + money(pr.amount_cents)) }}
          </button>
          <p v-if="startError" class="hpr-err">{{ startError }}</p>
          <p class="hpr-fine">Payable until {{ date(pr.valid_until) }}.</p>
        </template>

        <div v-else-if="confirming" class="hpr-note">
          Thank you. We are confirming your payment; this usually takes a few seconds.
        </div>

        <div v-else-if="pr.status === 'paid'" class="hpr-note hpr-note--ok">
          Paid on {{ date(pr.paid_at) }}. Thank you.
          <a v-if="pr.success_url" :href="pr.success_url" class="hpr-link">Continue</a>
        </div>

        <div v-else-if="pr.status === 'expired'" class="hpr-note">
          This request expired on {{ date(pr.valid_until) }}. Ask {{ pr.vendor_name }} to extend it.
        </div>

        <div v-else-if="pr.status === 'cancelled'" class="hpr-note">
          This request was withdrawn by {{ pr.vendor_name }}. Nothing is owed.
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesPaymentRequest — the public pay page of a payment request
// (/pay/request/{reference}; docs/payment-requests.md). The reference is the only
// capability. Each press of Pay asks Hermes for a fresh gateway order and hands
// the customer to it; coming back through the gateway's cancel link records the
// attempt as abandoned, and the link keeps working.
const REASONS = {
  card_declined: 'Your card was declined.',
  insufficient_funds: 'Your card was declined for insufficient funds.',
  expired_card: 'Your card has expired.',
  authentication_failed: 'Your bank could not confirm the payment.',
  abandoned: 'The last payment was not completed.',
  gateway_error: 'The payment service had a problem.',
  other: 'The last payment did not go through.',
};

export default {
  name: 'HermesPaymentRequest',
  props: {
    reference: { type: String, default: '' },
    apiBase: { type: String, default: '/api/v1' },
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      loading: true,
      notFound: false,
      loadError: '',
      pr: {},
      starting: false,
      startError: '',
      confirming: false,
      polls: 0,
    };
  },
  computed: {
    ref() {
      return this.reference || this.componentData.reference || '';
    },
    isOpen() {
      return this.pr.status === 'requested' || this.pr.status === 'failed';
    },
    failureText() {
      const reason = this.pr.last_attempt && this.pr.last_attempt.reason;
      return REASONS[reason] || REASONS.other;
    },
  },
  methods: {
    async load() {
      this.loadError = '';
      const attempt = new URLSearchParams(window.location.search).get('attempt');
      // Coming back from the gateway's cancel link: tell Hermes once, then tidy the address.
      const query = attempt === 'cancelled' ? '?attempt=cancelled' : '';
      try {
        const data = await this.api.get(`${this.apiBase}/pay/request/${encodeURIComponent(this.ref)}${query}`);
        this.pr = data.payment_request || {};
        if (attempt === 'cancelled') window.history.replaceState(null, '', window.location.pathname);
        // Back from a successful payment before the gateway has told Hermes: wait for it.
        this.confirming = attempt === 'paid' && this.isOpen && this.polls < 20;
        if (this.confirming) {
          this.polls += 1;
          setTimeout(() => this.load(), 3000);
        }
      } catch (e) {
        if (e?.response?.status === 404) this.notFound = true;
        else this.loadError = 'We could not load this payment request. Please try again in a moment.';
      } finally {
        this.loading = false;
      }
    },
    async pay() {
      if (this.starting) return;
      this.starting = true;
      this.startError = '';
      try {
        const data = await this.api.post(`${this.apiBase}/pay/request/${encodeURIComponent(this.ref)}/attempt`, {});
        if (data.pay_url) {
          window.location.href = data.pay_url;
          return;
        }
        this.startError = 'The payment could not be started. Please try again.';
      } catch (e) {
        if (e?.response?.status === 409) {
          await this.load();
        } else {
          this.startError = 'The payment could not be started. Please try again.';
        }
      }
      this.starting = false;
    },
    money(pence) {
      if (pence == null) return '—';
      return `£${(Number(pence) / 100).toFixed(2)}`;
    },
    date(v) {
      if (!v) return '';
      return new Date(Number(v) * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.hpr { min-height: 100vh; background: var(--h-bg-content, #F8FAFC); color: var(--h-text-primary, #0F172A); font-family: var(--h-font-body, system-ui); }
.hpr-head { background: var(--h-bg-card, #fff); border-bottom: 1px solid var(--h-border, #E2E8F0); }
.hpr-head__in { max-width: 560px; margin: 0 auto; padding: 16px 20px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.hpr-brand { font-weight: 600; font-size: 15px; }
.hpr-secure { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: var(--h-text-secondary, #64748B); }
.hpr-lock { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2; }

.hpr-wrap { max-width: 560px; margin: 0 auto; padding: 28px 16px 64px; }
.hpr-card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); padding: 26px 24px; }
.hpr-muted { color: var(--h-text-secondary, #64748B); }
.hpr-from { font-size: 13px; color: var(--h-text-secondary, #64748B); margin: 0 0 6px; }
.hpr-title { font-size: 21px; font-weight: 600; letter-spacing: -0.01em; margin: 0 0 18px; line-height: 1.3; }
.hpr-p { font-size: 14px; color: var(--h-text-secondary, #64748B); margin: 0 0 14px; line-height: 1.55; }

.hpr-sum { margin: 0 0 8px; border-top: 1px solid var(--h-border, #E2E8F0); }
.hpr-sum > div { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid var(--h-border, #E2E8F0); font-size: 14px; }
.hpr-sum dt { color: var(--h-text-secondary, #64748B); }
.hpr-sum dd { margin: 0; font-family: var(--h-font-mono, monospace); font-variant-numeric: tabular-nums; }
.hpr-sum__total dt { color: var(--h-text-primary, #0F172A); font-weight: 600; }
.hpr-sum__total dd { font-weight: 600; font-size: 16px; }
.hpr-fine { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin: 8px 0 0; }

.hpr-btn { margin-top: 20px; width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px; font: inherit; font-size: 15px; font-weight: 600; padding: 13px 18px; border-radius: var(--h-radius, 6px); border: none; cursor: pointer; background: var(--h-accent, #0EA5E9); color: #fff; }
.hpr-btn:hover:not(:disabled) { background: var(--h-accent-hover, #0284C7); }
.hpr-btn:disabled { opacity: 0.6; cursor: wait; }
.hpr-btn--ghost { background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-primary, #0F172A); width: auto; }
.hpr-btn__i { width: 16px; height: 16px; stroke: currentColor; fill: none; stroke-width: 2; }
.hpr-err { font-size: 13px; color: var(--h-danger-text, #B91C1C); margin: 10px 0 0; }

.hpr-note { margin-top: 18px; padding: 12px 14px; border-radius: var(--h-radius, 6px); background: var(--h-bg-subtle, #F1F5F9); font-size: 14px; line-height: 1.5; }
.hpr-note--ok { background: var(--h-success-bg, #ECFDF5); color: var(--h-success-text, #047857); }
.hpr-note--warn { background: var(--h-warning-bg, #FFFBEB); color: var(--h-warning-text, #B45309); margin-bottom: 0; }
.hpr-link { margin-left: 8px; color: inherit; font-weight: 600; }
</style>
